import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Caption,
  Card,
  Chart,
  Inline,
  LoadingState,
  Select,
  Stack,
  Table,
  Text
} from '@srichaitra/ui';
import { labels } from '@srichaitra/constants';
import {
  breakdownByCounterparty,
  listYears,
  loadActivity,
  monthLabels,
  monthlyTotals,
  selectTrendsEntries,
  selectTrendsError,
  selectTrendsForbidden,
  selectTrendsStatus,
  transactionsFor,
  useAppDispatch,
  useAppSelector
} from '@srichaitra/store';
import { Section } from './Section';
import { formatCompactCurrency, formatCurrency } from './format';

/**
 * The trend charts, with two levels of drill-down.
 *
 *   level 0   every month of a year, three measures side by side
 *   level 1   one month and one measure, broken down by counterparty
 *   level 2   the individual transactions behind one of those bars
 *
 * All three read from one activity feed already in the store, so a drill step
 * costs no request and the three levels can never disagree about the same
 * month.
 *
 * Every chart has a table view beside it. That is not a nicety: the aqua series
 * sits below the 3:1 contrast bar on a white surface, so a reader must have a
 * route to the values that does not depend on telling the colours apart. The
 * table is also what makes the figures selectable and searchable.
 */

const MEASURE_IDS = ['sales', 'purchases', 'misc'];

export function TrendsPanel() {
  const dispatch = useAppDispatch();
  const status = useAppSelector(selectTrendsStatus);
  const entries = useAppSelector(selectTrendsEntries);
  const error = useAppSelector(selectTrendsError);
  const forbidden = useAppSelector(selectTrendsForbidden);

  const [year, setYear] = useState(null);
  /** null at level 0; {month, measure} at level 1; plus {group} at level 2. */
  const [drill, setDrill] = useState(null);
  const [asTable, setAsTable] = useState(false);

  useEffect(() => {
    dispatch(loadActivity());
  }, [dispatch]);

  const years = useMemo(() => listYears(entries), [entries]);
  const months = useMemo(() => monthLabels(), []);

  // Settle on the most recent year with activity once the feed arrives, and
  // leave the user's own choice alone afterwards.
  useEffect(() => {
    if (year === null && years.length > 0) setYear(years[0]);
  }, [year, years]);

  // Nothing at all for a role without the History tile — the feed this is drawn
  // from is not available to them, and an empty section with a heading would
  // just be furniture.
  if (forbidden) return null;

  // The rest keep the section heading, so the page keeps its shape while the
  // data loads or fails rather than shifting everything up and back down.
  if (status === 'failed') {
    return (
      <Section title={labels.trends.heading}>
        <Alert tone="error">{error?.message}</Alert>
      </Section>
    );
  }
  if (status === 'idle' || (status === 'loading' && entries.length === 0)) {
    return (
      <Section title={labels.trends.heading}>
        <LoadingState message={labels.common.loading} />
      </Section>
    );
  }
  if (years.length === 0) {
    return (
      <Section title={labels.trends.heading}>
        <Alert tone="info">{labels.trends.noActivity}</Alert>
      </Section>
    );
  }

  const activeYear = year ?? years[0];

  return (
    <Section
      title={labels.trends.heading}
      /* The controls sit in the section header, not in the chart card: the year
         scopes this whole section — chart, breakdown and table alike — and it
         must not look like it also filters the profit figures above, which are
         all-time. */
      actions={
        <>
          <Select
            label={labels.trends.year}
            value={String(activeYear)}
            onChange={(event) => {
              setYear(Number(event.target.value));
              // A drill-down is anchored to a month of a particular year;
              // keeping it while the year changes would show one year's chart
              // over another year's transactions.
              setDrill(null);
            }}
            options={years.map((value) => ({ value: String(value), label: String(value) }))}
            fullWidth={false}
            size="small"
            sx={{ minWidth: 120 }}
          />
          <Button variant="secondary" size="sm" onClick={() => setAsTable((current) => !current)}>
            {asTable ? labels.trends.showChart : labels.trends.showTable}
          </Button>
        </>
      }
    >
      <Stack gap={3}>
        <Breadcrumbs
          drill={drill}
          months={months}
          year={activeYear}
          onReset={() => setDrill(null)}
          onUp={() => setDrill({ month: drill.month, measure: drill.measure })}
        />

        <Card padding={4}>
          {drill?.group ? (
            <TransactionLevel entries={entries} year={activeYear} drill={drill} />
          ) : drill ? (
            <BreakdownLevel
              entries={entries}
              year={activeYear}
              drill={drill}
              months={months}
              asTable={asTable}
              loading={status === 'loading'}
              onSelectGroup={(group) => setDrill({ ...drill, ...group })}
            />
          ) : (
            <OverviewLevel
              entries={entries}
              year={activeYear}
              months={months}
              asTable={asTable}
              loading={status === 'loading'}
              onSelectMonth={(next) => setDrill(next)}
            />
          )}
        </Card>
      </Stack>
    </Section>
  );
}

/** Where the reader is, and the way back up. Rendered only below level 0. */
function Breadcrumbs({ drill, months, year, onReset, onUp }) {
  if (!drill) return null;

  const measureLabel = labels.trends[drill.measure];

  return (
    <Inline gap={1}>
      <Button variant="ghost" size="sm" onClick={onReset}>
        {labels.trends.backToOverview}
      </Button>
      <Caption>›</Caption>
      {drill.group ? (
        <>
          <Button variant="ghost" size="sm" onClick={onUp}>
            {`${months[drill.month]} ${year} · ${measureLabel}`}
          </Button>
          <Caption>›</Caption>
          <Caption>{drill.group}</Caption>
        </>
      ) : (
        <Caption>{`${months[drill.month]} ${year} · ${measureLabel}`}</Caption>
      )}
    </Inline>
  );
}

/* --- Level 0: the year, month by month ---------------------------------- */

function OverviewLevel({ entries, year, months, asTable, loading, onSelectMonth }) {
  const rows = useMemo(() => monthlyTotals(entries, year), [entries, year]);

  const series = MEASURE_IDS.map((id) => ({
    id,
    label: labels.trends[id],
    data: rows.map((row) => row[id])
  }));

  return (
    <Stack gap={2}>
      {asTable ? (
        <Table
          caption={`${labels.trends.heading} ${year}`}
          columns={[
            { key: 'month', header: labels.trends.columns.month, render: (row) => months[row.month] },
            ...MEASURE_IDS.map((id) => ({
              key: id,
              header: labels.trends[id],
              render: (row) => formatCurrency(row[id], { whole: true })
            }))
          ]}
          rows={rows}
          rowKey={(row) => row.month}
          emptyMessage={labels.trends.noActivity}
        />
      ) : (
        <Chart
          categories={months}
          series={series}
          height={340}
          loading={loading}
          valueFormatter={(value) => formatCurrency(value, { whole: true })}
          axisFormatter={formatCompactCurrency}
          ariaLabel={`${labels.trends.heading} ${year}`}
          onSelect={({ seriesId, index }) => onSelectMonth({ month: index, measure: seriesId })}
        />
      )}

      <Caption>{labels.trends.monthlyCaption}</Caption>
      {asTable ? null : <Caption>{labels.trends.drillHint}</Caption>}
    </Stack>
  );
}

/* --- Level 1: one month, one measure, by counterparty -------------------- */

function BreakdownLevel({ entries, year, drill, months, asTable, loading, onSelectGroup }) {
  const rows = useMemo(
    () =>
      breakdownByCounterparty(entries, {
        year,
        month: drill.month,
        measure: drill.measure,
        otherLabel: labels.trends.other
      }),
    [entries, year, drill.month, drill.measure]
  );

  const title = `${labels.trends[drill.measure]} · ${months[drill.month]} ${year} ${
    labels.trends.byGroup[drill.measure]
  }`;

  if (rows.length === 0) return <Alert tone="info">{labels.trends.noActivity}</Alert>;

  return (
    <Stack gap={2}>
      <Text weight="medium">{title}</Text>

      {asTable ? (
        <Table
          caption={title}
          columns={[
            { key: 'name', header: labels.trends.columns.name },
            { key: 'count', header: labels.trends.columns.count },
            {
              key: 'total',
              header: labels.trends.columns.amount,
              render: (row) => formatCurrency(row.total, { whole: true })
            }
          ]}
          rows={rows}
          rowKey={(row) => row.name}
          emptyMessage={labels.trends.noActivity}
        />
      ) : (
        <Chart
          horizontal
          // Passed in ranked order, largest first. Material UI draws a
          // horizontal band axis top-down, so the biggest bar lands at the top
          // where a ranking is read from.
          categories={rows.map((row) => row.name)}
          series={[
            {
              id: 'total',
              label: labels.trends[drill.measure],
              data: rows.map((row) => row.total)
            }
          ]}
          height={Math.max(220, rows.length * 44 + 60)}
          loading={loading}
          valueFormatter={(value) => formatCurrency(value, { whole: true })}
          axisFormatter={formatCompactCurrency}
          ariaLabel={title}
          onSelect={({ index }) => {
            const row = rows[index];
            onSelectGroup({ group: row.name, members: row.members });
          }}
        />
      )}

      {asTable ? null : <Caption>{labels.trends.drillHintLevel2}</Caption>}
    </Stack>
  );
}

/* --- Level 2: the transactions behind one bar ---------------------------- */

function TransactionLevel({ entries, year, drill }) {
  const rows = useMemo(
    () =>
      transactionsFor(entries, {
        year,
        month: drill.month,
        measure: drill.measure,
        group: drill.group,
        members: drill.members
      }),
    [entries, year, drill]
  );

  const title = `${labels.trends.transactionsHeading} · ${drill.group}`;

  return (
    <Stack gap={2}>
      <Text weight="medium">{title}</Text>

      {drill.members ? <Caption>{labels.trends.otherMembers(drill.members.length)}</Caption> : null}

      {/* A table, not a chart: individual transactions are records to read, and
          a bar per row would encode nothing the amount column does not. */}
      <Table
        caption={title}
        columns={[
          {
            key: 'at',
            header: labels.trends.columns.date,
            render: (row) => new Date(row.at).toLocaleDateString()
          },
          {
            key: 'what',
            header:
              drill.measure === 'misc'
                ? labels.trends.columns.description
                : labels.trends.columns.counterparty,
            render: (row) => (drill.measure === 'misc' ? row.description : row.counterparty)
          },
          { key: 'recordedBy', header: labels.trends.columns.recordedBy },
          {
            key: 'total',
            header: labels.trends.columns.amount,
            render: (row) => formatCurrency(row.total)
          }
        ]}
        rows={rows}
        rowKey={(row) => row.id}
        emptyMessage={labels.trends.noActivity}
      />
    </Stack>
  );
}
